import PageLoader from "@/components/loader/PageLoader";
import { User, useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface Branch {
  id: number;
  name: string;
  is_open: boolean;
  points_to_currency_multiplier: string;
  points_conversion_rate: string;
  min_points_to_redeem: string;
  max_points_to_redeem: string;
  loyalty_program_enabled: boolean;
  min_order_to_redeem_points: string;
  upsell_feature: boolean;
  timezone: string;
}

export default function BranchComponent() {
  const { t } = useTranslation();
  const { user, login } = useUser();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [axiosInstance, loading, isFetching] = useAxiosInstance();

  // Function to fetch branches by business name
  // Function to fetch all branches by business name with pagination
  const fetchBranchesByBusinessName = useCallback(async () => {
    try {
      const storedUserData = localStorage.getItem("user_data");
      const userData = storedUserData ? JSON.parse(storedUserData) : null;
      const businessName = userData?.owner?.business_name;

      // Check if axiosInstance, businessName, and loading state are ready
      if (!loading && axiosInstance && businessName) {
        let page = 1;
        let allBranches: Branch[] = [];
        let hasMorePages = true;

        // Loop through pages to fetch all branches
        while (hasMorePages) {
          const apiUrl = `${
            import.meta.env.VITE_APP_API_BASE_URL
          }api/v1/branch/?business_name=${businessName}&page=${page}`;
          const response = await axiosInstance.get(apiUrl);

          // Concatenate branches from the current page
          allBranches = [...allBranches, ...response.data.results];

          // Check if there are more pages
          if (response.data.next) {
            page++;
          } else {
            hasMorePages = false;
          }
        }

        setBranches(allBranches);

        // Auto-select the branch if only one is available
        if (allBranches.length === 1) {
          handleSelectBranch(allBranches[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [loading, axiosInstance, user]);

  // Fetch branches on component mount or when dependencies change
  useEffect(() => {
    fetchBranchesByBusinessName();
  }, [fetchBranchesByBusinessName]);

  useEffect(() => {
    if (selectedBranch) {
      updateSelectedBranchInStorage(selectedBranch);
    }
  }, [selectedBranch]);

  // Function to handle branch selection
  const handleSelectBranch = (branch: Branch) => {
    setSelectedBranch(branch);
  };

  // Helper function to update selectedBranch in local storage and user context
  const updateSelectedBranchInStorage = (branch: Branch) => {
    const storedUserData = localStorage.getItem("user_data");
    if (storedUserData) {
      const userData: User = JSON.parse(storedUserData);
      userData.selectedBranch = branch;
      localStorage.setItem("user_data", JSON.stringify(userData));
      login(userData);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto mt-5 p-4">
        <div
          className="mx-auto my-8 flex h-auto w-full flex-col items-center justify-center rounded-lg bg-white p-4 shadow"
          style={{ width: "100%", minHeight: "50vh" }}
        >
          {branches.length > 0 ? (
            <>
              <h2 className="mb-4 text-xl font-semibold">
                {t("back.management.dashboard.branch.selectbranch")}
              </h2>
              <select
                value={selectedBranch?.id || ""}
                onChange={(e) => {
                  const branchId = Number(e.target.value);
                  const branch = branches.find(
                    (b: Branch) => b.id === branchId,
                  );
                  if (branch) {
                    handleSelectBranch(branch);
                  }
                }}
                className="mt-1 w-3/4 rounded-md border-gray-300 text-gray-600 md:w-1/5"
              >
                <option value="">
                  {t("back.management.dashboard.branch.selectbranchbutton")}
                </option>
                {branches.map((branch: Branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <div className="flex flex-col items-center">
              <FontAwesomeIcon
                icon={faPlus}
                className="mb-4 text-3xl text-red-500"
              />
              <h2 className="mb-4 text-xl font-semibold">
                {t("back.management.dashboard.branch.nobranch")}
              </h2>
              <Link
                to="/branches/add"
                className="rounded-full bg-red-500 px-4 py-2 font-bold text-white"
              >
                {t("back.management.dashboard.branch.addbranch")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </PageLoader>
  );
}
